import { AxiosInstance } from 'axios'
import {
  BulkCategoryResponse,
  BulkTagResponse,
  ExpenseCategory,
  Tag
} from 'types/tags-categories'
import { TAGS_ENDPOINTS } from 'api/ApiRoutes'
import { Group, Team } from 'types/groups'
import { SortByCategories, SortDirection } from 'types/misc'
import { ID, PaginatedResult } from 'types/utility'

class TagsApi {
  constructor(private axiosInstance: AxiosInstance) {}

  uploadCsvBulkTags = async ({ file }: { file: File }) => {
    const formData = new FormData()
    formData.append('file', file)
    const d = await this.axiosInstance.post<BulkTagResponse>(
      TAGS_ENDPOINTS.BATCH_TAGS,
      formData
    )
    return d.data
  }

  getCSVTagsTemplate = () => {
    return this.axiosInstance
      .get(TAGS_ENDPOINTS.BATCH_TAGS_TEMPLATE)
      .then((d) => d.data)
  }

  getCSVCategoriesTemplate = async () => {
    const d = await this.axiosInstance.get(
      TAGS_ENDPOINTS.BATCH_CATEGORIES_TEMPLATE
    )
    return d.data
  }

  getTags = async ({
    team_token_id,
    page = 1,
    page_size = 25,
    q = '',
    sort_by = 'name',
    sort_direction = 'asc',
    archived = false
  }: {
    team_token_id?: Team['token_id']
    page_size?: number
    page?: number
    q?: string
    sort_by?: SortByCategories
    sort_direction?: SortDirection
    archived?: boolean
  }) => {
    const d = await this.axiosInstance.get<PaginatedResult<Tag>>(
      TAGS_ENDPOINTS.BASE,
      {
        params: {
          page_size,
          q,
          sort_by,
          sort_direction,
          page,
          team_token_id,
          paginate: 'by_page',
          archived
        }
      }
    )
    return d.data
  }

  getTeamCategories = async ({
    team_token_id,
    page = 1,
    page_size = 25,
    q = '',
    sort_by,
    sort_direction,
    archived = false
  }: {
    team_token_id: Team['token_id']
    page_size?: number
    page?: number
    q?: string
    sort_by?: SortByCategories
    sort_direction?: SortDirection
    archived?: boolean
  }) => {
    const d = await this.axiosInstance.get<PaginatedResult<ExpenseCategory>>(
      TAGS_ENDPOINTS.TEAM_CATEGORIES(team_token_id),
      {
        params: { page_size, q, sort_by, sort_direction, page, archived }
      }
    )
    return d.data
  }

  createTag = async ({
    tag,
    teamId
  }: {
    tag: Partial<Tag>
    teamId: Group['token_id']
  }) => {
    const d = await this.axiosInstance.post(TAGS_ENDPOINTS.BASE, {
      tag,
      team_token_id: teamId
    })
    return d.data
  }

  createCategory = async ({
    category,
    teamId
  }: {
    category: Partial<ExpenseCategory>
    teamId: Team['token_id']
  }) => {
    const d = await this.axiosInstance.post(
      TAGS_ENDPOINTS.TEAM_CATEGORIES(teamId),
      {
        expense_category: category
      }
    )
    return d.data
  }

  uploadCsvBulkCategories = async ({
    file,
    teamId
  }: {
    file: File
    teamId: Team['token_id']
  }) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('team_token_id', teamId as string)
    const d = await this.axiosInstance.post<BulkCategoryResponse>(
      TAGS_ENDPOINTS.BATCH_CATEGORIES,
      formData
    )
    return d.data
  }

  deleteTag = ({ tagId, teamId }: { tagId: ID; teamId: ID }) => {
    return this.axiosInstance.delete(
      `${TAGS_ENDPOINTS.TAG(tagId)}?team_token_id=${teamId}`
    )
  }

  reactivateTag = async ({ tagId, teamId }: { tagId: ID; teamId: ID }) => {
    const d = await this.axiosInstance.put(
      `${TAGS_ENDPOINTS.TAG(tagId)}?team_token_id=${teamId}`,
      {
        archived: false
      }
    )
    return d.data
  }

  deleteCategory = ({
    teamId,
    catId
  }: {
    teamId: Team['token_id']
    catId: ID
  }) => {
    return this.axiosInstance.delete(TAGS_ENDPOINTS.CATEGORY(teamId, catId))
  }

  reactivateCategory = async ({ catId, teamId }: { catId: ID; teamId: ID }) => {
    const d = await this.axiosInstance.put(
      TAGS_ENDPOINTS.CATEGORY(teamId, catId),
      {
        archived: false
      }
    )
    return d.data
  }
}

export default TagsApi
